<template>
  <div>
    <Breadcrumbs main="Accounts" title="Billing" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Billing</h5>
                <span class="text-muted">Manage matter billing records and payment status</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadBills"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Bill
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading bills...
              </div>
              <div v-else-if="bills.length === 0" class="text-center py-4 text-muted">
                No bills found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Invoice No.</th>
                      <th>Matter</th>
                      <th>Description</th>
                      <th class="text-end">Amount</th>
                      <th class="text-end">Paid</th>
                      <th class="text-end">Balance</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="bill in bills" :key="bill.billId">
                      <td>
                        <strong>{{ bill.invoiceNo || `#${bill.billId}` }}</strong>
                      </td>
                      <td>{{ matterLabel(bill.matterId) }}</td>
                      <td>{{ bill.description || '—' }}</td>
                      <td class="text-end">{{ formatMoney(bill.amount) }}</td>
                      <td class="text-end">{{ formatMoney(bill.amountPaid) }}</td>
                      <td class="text-end">{{ formatMoney(bill.balance) }}</td>
                      <td>
                        <span class="badge" :class="statusBadgeClass(bill.status)">
                          {{ bill.status || '—' }}
                        </span>
                      </td>
                      <td>{{ formatDate(bill.createdAt) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(bill)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(bill)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Bill' : 'Add Bill' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Invoice number</label>
                      <input
                        v-model="form.invoiceNo"
                        class="form-control"
                        type="text"
                        maxlength="50"
                        placeholder="e.g. BILL-2026-001"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Matter</label>
                      <select v-model.number="form.matterId" class="form-select">
                        <option :value="null">None</option>
                        <option
                          v-for="matter in matters"
                          :key="matter.matterId"
                          :value="matter.matterId"
                        >
                          {{ matterLabel(matter.matterId) }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Amount</label>
                      <input
                        v-model.number="form.amount"
                        class="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                      >
                    </div>
                  </div>
                  <div v-if="isEditing" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Amount paid</label>
                      <input
                        v-model.number="form.amountPaid"
                        class="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                      >
                    </div>
                  </div>
                  <div v-if="isEditing" class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Status</label>
                      <select v-model="form.status" class="form-select">
                        <option value="">Auto (from amount paid)</option>
                        <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Description</label>
                      <textarea
                        v-model="form.description"
                        class="form-control"
                        rows="3"
                        maxlength="255"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Bill') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="billToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Bill</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="billToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete bill
                <strong>{{ billToDelete.invoiceNo || `#${billToDelete.billId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The bill will be soft-deleted and hidden from the list. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="billToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="billToDelete" class="modal-backdrop fade show" @click="billToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listMatters } from '@/services/matterService'
import {
  listBills,
  createBill,
  updateBill,
  deleteBill,
} from '@/services/billService'

const STATUS_OPTIONS = ['UNPAID', 'PARTIAL', 'PAID']

function hasBillPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const billPriv = user.privileges.find((p) => p.entity === 'BILL')
  if (!billPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return billPriv[map[action]] === true
}

const emptyForm = () => ({
  invoiceNo: '',
  matterId: null,
  amount: null,
  amountPaid: null,
  status: '',
  description: '',
})

export default {
  name: 'AccountsBilling',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasBillPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasBillPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasBillPrivilege(this.currentUser, 'delete')
    },

    statusOptions() {
      return STATUS_OPTIONS
    },
  },

  data() {
    return {
      bills: [],
      matters: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      originalMatterId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      billToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadBills()
    this.loadMatters()
  },

  methods: {
    async loadBills() {
      this.loading = true
      this.error = ''

      try {
        const data = await listBills()
        this.bills = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view bills.'
        } else {
          this.error = err.message || 'Failed to load bills'
        }
      } finally {
        this.loading = false
      }
    },

    async loadMatters() {
      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch {
        this.matters = []
      }
    },

    matterLabel(matterId) {
      if (!matterId) return '—'
      const matter = this.matters.find((m) => m.matterId === matterId)
      if (!matter) return `#${matterId}`
      return matter.caseTitle || matter.caseNo || `Matter #${matterId}`
    },

    statusBadgeClass(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'PAID') return 'badge-success'
      if (normalized === 'PARTIAL') return 'badge-warning'
      if (normalized === 'UNPAID') return 'badge-danger'
      return 'badge-light-primary'
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    formatMoney(value) {
      if (value == null || Number.isNaN(Number(value))) return '—'
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.originalMatterId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(bill) {
      this.isEditing = true
      this.editingId = bill.billId
      this.originalMatterId = bill.matterId || null
      this.form = {
        invoiceNo: bill.invoiceNo || '',
        matterId: bill.matterId || null,
        amount: bill.amount ?? null,
        amountPaid: bill.amountPaid ?? null,
        status: bill.status || '',
        description: bill.description || '',
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {}

      const invoiceNo = this.form.invoiceNo?.trim()
      if (invoiceNo) payload.invoiceNo = invoiceNo

      const currentMatterId = this.form.matterId || null
      if (this.isEditing) {
        if (currentMatterId !== this.originalMatterId) {
          payload.matterId = currentMatterId
        }
      } else if (currentMatterId) {
        payload.matterId = currentMatterId
      }

      if (this.form.amount != null && this.form.amount !== '') {
        payload.amount = Number(this.form.amount)
      }

      if (this.isEditing && this.form.amountPaid != null && this.form.amountPaid !== '') {
        payload.amountPaid = Number(this.form.amountPaid)
      }

      const description = this.form.description?.trim()
      if (description) payload.description = description

      if (this.isEditing && this.form.status) {
        payload.status = this.form.status
      }

      return payload
    },

    async submitForm() {
      this.formError = ''
      this.saving = true

      try {
        if (this.isEditing) {
          await updateBill({
            billId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createBill(this.buildPayload())
        }
        this.showFormModal = false
        await this.loadBills()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(bill) {
      this.billToDelete = bill
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteBill(this.billToDelete.billId)
        this.billToDelete = null
        await this.loadBills()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete bill'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
